<template>
    <section v-if="visible" class="delivery-notifications" :class="`delivery-notifications--${state}`">
        <span class="delivery-notifications__icon flex-shrink-0" aria-hidden="true">
            <BootstrapIcon :name="state === 'enabled' ? 'bell-fill' : 'bell'" :size="18" />
        </span>
        <div class="delivery-notifications__copy">
            <strong class="delivery-notifications__title">{{title}}</strong>
            <span class="delivery-notifications__description">{{description}}</span>
        </div>
        <button
        v-if="state === 'enabled'"
        type="button"
        class="delivery-notifications__button btn btn-sm btn-outline-secondary"
        :disabled="busy"
        @click="disableNotifications"
        >{{$t('delivery.push_disable')}}</button>
        <a
        v-else-if="showSetupGuide"
        class="delivery-notifications__button delivery-notifications__guide btn btn-sm btn-outline-secondary"
        :href="setupGuideUrl"
        target="_blank"
        rel="noopener"
        >{{$t('delivery.push_setup_guide')}}</a>
        <button
        v-else-if="canEnable"
        type="button"
        class="delivery-notifications__button btn btn-sm btn-primary"
        :disabled="busy"
        @click="enableNotifications"
        >
            <span v-if="busy" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
            <span v-else>{{$t('delivery.push_enable')}}</span>
        </button>
    </section>
</template>

<script>
import BootstrapIcon from '@/common/directive/BootstrapIcon'
import { TLD } from '@/common/constants.js'

export default {
    components: { BootstrapIcon },
    data: function () {
        return {
            busy: false,
            configLoaded: false,
            publicKey: null,
            pushEnabled: false,
            state: 'disabled',
            TLD
        }
    },
    computed: {
        supported: function () {
            return typeof window !== 'undefined' &&
                typeof navigator !== 'undefined' &&
                'serviceWorker' in navigator &&
                'PushManager' in window &&
                'Notification' in window
        },
        visible: function () {
            return this.configLoaded && this.pushEnabled
        },
        canEnable: function () {
            return this.supported && this.state !== 'denied' && this.state !== 'unsupported'
        },
        isAppleMobile: function () {
            if (typeof navigator === 'undefined') return false
            return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
        },
        isAndroid: function () {
            if (typeof navigator === 'undefined') return false
            return /Android/i.test(navigator.userAgent)
        },
        isStandalone: function () {
            if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
            return navigator.standalone === true ||
                (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches)
        },
        showSetupGuide: function () {
            return this.state === 'unsupported' &&
                (this.isAppleMobile || this.isAndroid) &&
                !this.isStandalone
        },
        setupGuideUrl: function () {
            const isRomanian = this.TLD.endsWith('.ro')
            const domain = isRomanian ? 'tapgiant.ro' : 'tapgiant.com'
            const device = this.isAndroid ? 'android' : 'iphone'
            const path = isRomanian
                ? `/activeaza-notificarile-pentru-livrari-${device}`
                : `/enable-delivery-notifications-${device}`
            return `https://www.${domain}${path}`
        },
        title: function () {
            return this.$t(`delivery.push_${this.state}_title`)
        },
        description: function () {
            return this.$t(`delivery.push_${this.state}_description`)
        },
        locale: function () {
            return this.$store.state.locale === 'ro' ? 'ro' : 'en'
        }
    },
    watch: {
        locale: function () {
            if (this.state === 'enabled') this.syncExistingSubscription()
        }
    },
    mounted: async function () {
        await this.loadConfiguration()
    },
    methods: {
        loadConfiguration: async function () {
            const response = await this.$store.dispatch('GET_PUSH_CONFIG')
            this.configLoaded = true
            this.pushEnabled = Boolean(response && response.status === 200 && response.data && response.data.enabled)
            this.publicKey = this.pushEnabled && typeof response.data.public_key === 'string' ? response.data.public_key : null

            if (!this.pushEnabled) return
            if (!this.supported) {
                this.state = 'unsupported'
                return
            }
            if (Notification.permission === 'denied') {
                this.state = 'denied'
                return
            }

            await this.syncExistingSubscription()
        },
        getRegistration: async function () {
            return navigator.serviceWorker.ready
        },
        getSubscriptionPayload: function (subscription) {
            const value = subscription.toJSON()
            return {
                endpoint: value.endpoint,
                expirationTime: value.expirationTime || null,
                keys: value.keys,
                locale: this.locale
            }
        },
        syncExistingSubscription: async function () {
            try {
                const registration = await this.getRegistration()
                const subscription = await registration.pushManager.getSubscription()
                if (!subscription || !this.subscriptionUsesCurrentKey(subscription)) {
                    this.state = 'disabled'
                    return
                }

                const response = await this.$store.dispatch('PUSH_SUBSCRIBE', this.getSubscriptionPayload(subscription))
                this.state = response && response.status === 200 ? 'enabled' : 'error'
            } catch (error) {
                this.state = 'error'
            }
        },
        enableNotifications: async function () {
            if (!this.canEnable || this.busy) return
            this.busy = true

            try {
                const permission = await Notification.requestPermission()
                if (permission !== 'granted') {
                    this.state = permission === 'denied' ? 'denied' : 'disabled'
                    return
                }

                const registration = await this.getRegistration()
                let subscription = await registration.pushManager.getSubscription()
                if (subscription && !this.subscriptionUsesCurrentKey(subscription)) {
                    await this.$store.dispatch('PUSH_UNSUBSCRIBE', { endpoint: subscription.endpoint })
                    await subscription.unsubscribe()
                    subscription = null
                }
                if (!subscription) {
                    subscription = await registration.pushManager.subscribe({
                        userVisibleOnly: true,
                        applicationServerKey: this.urlBase64ToUint8Array(this.publicKey)
                    })
                }

                const response = await this.$store.dispatch('PUSH_SUBSCRIBE', this.getSubscriptionPayload(subscription))
                if (response && response.status === 200) {
                    this.state = 'enabled'
                    this.$bus.$emit('Notification/show', {
                        title: this.$t('notifications.notifications_enabled'),
                        msg: this.$t('notifications.notifications_enabled_description'),
                        level: 'success'
                    })
                } else {
                    this.state = 'error'
                }
            } catch (error) {
                this.state = Notification.permission === 'denied' ? 'denied' : 'error'
            } finally {
                this.busy = false
            }
        },
        disableNotifications: async function () {
            if (this.busy) return
            this.busy = true

            try {
                const registration = await this.getRegistration()
                const subscription = await registration.pushManager.getSubscription()
                if (subscription) {
                    await this.$store.dispatch('PUSH_UNSUBSCRIBE', { endpoint: subscription.endpoint })
                    await subscription.unsubscribe()
                }
                this.state = 'disabled'
            } catch (error) {
                this.state = 'error'
            } finally {
                this.busy = false
            }
        },
        subscriptionUsesCurrentKey: function (subscription) {
            const current = subscription.options && subscription.options.applicationServerKey
            if (!current || !this.publicKey) return false

            const currentBytes = new Uint8Array(current)
            const expectedBytes = this.urlBase64ToUint8Array(this.publicKey)
            return currentBytes.length === expectedBytes.length && currentBytes.every(function (value, index) {
                return value === expectedBytes[index]
            })
        },
        urlBase64ToUint8Array: function (base64String) {
            const padding = '='.repeat((4 - base64String.length % 4) % 4)
            const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
            const rawData = window.atob(base64)
            return Uint8Array.from(rawData, character => character.charCodeAt(0))
        }
    }
}
</script>
